import styled from 'styled-components';
import { COL_BACKGROUNDS } from '@/modules/vip/ComparisonSection/helpers';
import { device, hexToRGB, typography } from '@/styles/helpers';

export const ScComparisonSlider = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
`;

export const ScSwipeHint = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  ${typography.body1regular};
  color: ${({ theme }) => hexToRGB(theme.whiteColor, 0.6)};
  margin-bottom: 32px;
`;

export const ScSliderLayout = styled.div`
  display: flex;
  gap: 4px;
  align-items: flex-start;
`;

export const ScLabelColumn = styled.div`
  flex: 0 0 140px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const ScSlideViewport = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

export const ScSlidesList = styled.div`
  display: flex;
  touch-action: pan-y;
  user-select: none;
  column-gap: 4px;
`;

const SLIDE_GAP = 4;

export const ScSlide = styled.div<{ $perView: number }>`
  flex: 0 0
    ${({ $perView }) =>
      `calc((100% - ${($perView - 1) * SLIDE_GAP}px) / ${$perView})`};
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const ScSlideHeaderRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 4px;
`;

export const ScSlideLabelHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 8px 12px 0;
  ${typography.body1bold};
  color: ${({ theme }) => theme.whiteColor};
`;

export const ScSlideColCard = styled.div<{ $colIndex: number }>`
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: 8px 12px;
  padding: 12px;
  border-radius: 8px;
  background: ${({ $colIndex }) =>
    COL_BACKGROUNDS[$colIndex] ?? COL_BACKGROUNDS[0]};

  @media (max-width: 340px) {
    grid-template-columns: 1fr;
    justify-items: center;
  }
`;

export const ScSlideColBadge = styled.div`
  width: 52px;
  height: 52px;
  flex-shrink: 0;
`;

export const ScSlideColTextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  @media (max-width: 340px) {
    align-items: center;
    text-align: center;
  }
`;

export const ScSlideColTitle = styled.span`
  ${typography.headline3};
  color: ${({ theme }) => theme.whiteColor};
  text-align: center;
  text-transform: uppercase;
`;

export const ScSectionTitle = styled.div`
  display: flex;
  align-items: center;
  min-height: 32px;
  padding: 4px 0;
  font-family: Inter, sans-serif;
  ${typography.caption1bold};
  color: ${({ theme }) => theme.whiteColor};
  text-transform: uppercase;
  letter-spacing: 0.01em;
`;

export const ScSlideRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const ScLabelCell = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  min-height: 48px;
  padding: 0 24px;
  @media (${device.mobileS}) {
    padding: 0;
  }
  ${typography.body1regular};
  color: ${({ theme }) => theme.blackLightColor};
`;

export const ScValueCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  padding: 12px 8px;
  background: ${({ theme }) => theme.grayDarkColor};
  border-radius: 8px;
  text-align: center;
  ${typography.body1regular};
  color: ${({ theme }) => theme.whiteColor};
`;

export const ScProgressTrack = styled.div<{ $steps: number }>`
  position: relative;
  width: 140px;
  height: 4px;
  border-radius: 360px;
  background: ${({ theme }) => theme.grayDarkColor};
  align-self: flex-end;
`;

export const ScProgressIndicator = styled.div<{
  $step: number;
  $steps: number;
}>`
  position: absolute;
  top: 0;
  left: ${({ $step, $steps }) => ($step / $steps) * 100}%;
  width: ${({ $steps }) => 100 / $steps}%;
  height: 4px;
  border-radius: 360px;
  background: ${({ theme }) => theme.flameOrange};
  transition: left 0.3s ease;
`;

export const ScArrows = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 24px;
  margin-top: 8px;
`;

export const ScArrow = styled.button<{
  $disabled: boolean;
  $flipped?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: none;
  border: none;
  padding: 0;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.3 : 1)};
  transition: opacity 0.2s;
  color: ${({ theme }) => theme.whiteColor};
  ${({ $flipped }) => $flipped && 'transform: scaleX(-1);'}
`;

export const ScSliderControls = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 8px;
`;
